import isNil from 'lodash/isNil';
import type {
  BulkPatientStatementDTO,
} from 'dtos/bulkPatientStatementDTO';
import type {
  ReportDTO,
} from 'dtos/reportDTO';
import type {
  ReportOutputDTO,
} from 'dtos/reportOutputDTO';
import type {
  ActivePanel,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  formatFileSize,
} from 'utils/string';
import type {
  ReportState,
  RunOutput,
  RunOutputFile,
  RunOutputPanelInfo,
  RunOutputSource,
  SummaryTone,
} from 'views/EMR/components/Panel/RunOutputPanel/types';
import {
  isNilOrZero,
} from '@populateui/utils';
import compact from 'lodash/compact';
import {
  DEFAULT_CONNECTOR,
} from 'core/constants';

export const RUN_OUTPUT_PANEL_TYPE = 'runOutput';

export function getRunId(source: RunOutputSource): number {
  return source.kind === 'report' ? source.reportId : source.bulkPatientStatementId;
}

type ReportRunParams = { reportId: number; reportOutputFileId?: number };
type StatementRunParams = { bulkPatientStatementId: number; reportOutputFileId?: number };

// A null file id means "the run's first file", so leave the param out instead of sending null.
export function getRunParams(
  source: Extract<RunOutputSource, { kind: 'report' }>,
  reportOutputFileId?: number | null,
): ReportRunParams;
export function getRunParams(
  source: Extract<RunOutputSource, { kind: 'statement' }>,
  reportOutputFileId?: number | null,
): StatementRunParams;
export function getRunParams(
  source: RunOutputSource,
  reportOutputFileId?: number | null,
): ReportRunParams | StatementRunParams;
export function getRunParams(
  source: RunOutputSource,
  reportOutputFileId?: number | null,
): ReportRunParams | StatementRunParams {
  const fileParam = isNil(reportOutputFileId) ? {} : { reportOutputFileId };
  return source.kind === 'report'
    ? { reportId: source.reportId, ...fileParam }
    : { bulkPatientStatementId: source.bulkPatientStatementId, ...fileParam };
}

export function formatFileMeta(file: Pick<RunOutputFile, 'itemCount' | 'sizeBytes'>): string {
  return compact([
    isNil(file.sizeBytes) ? null : formatFileSize(file.sizeBytes),
    isNilOrZero(file.itemCount) ? null : `${file.itemCount?.toLocaleString('en-US')} Rows`,
  ]).join(DEFAULT_CONNECTOR);
}

export function formatRunSummaryLine(fileCount: number, exceptionCount: number): string {
  let files = `${fileCount} files`;
  if (fileCount === 0) {
    files = 'No file';
  } else if (fileCount === 1) {
    files = '1 file';
  }
  return exceptionCount > 0 ? `${files} · ${exceptionCount} exceptions` : files;
}

export function getRunPanelIdentity(source: RunOutputSource): string {
  return `${source.kind}:${getRunId(source)}`;
}

// ActivePanel.entityKey is the registered getPanelIdentity(panelInfo), i.e. getRunPanelIdentity.
export function isRunPanelOpen(activePanel: ActivePanel | null, source: RunOutputSource): boolean {
  return activePanel?.type === RUN_OUTPUT_PANEL_TYPE
    && activePanel.entityKey === getRunPanelIdentity(source);
}

export function canOpenRun(state: string | null | undefined): boolean {
  return state !== 'InProgress';
}

export function buildReportPanelInfo(row: ReportDTO): RunOutputPanelInfo {
  return {
    kind: 'report',
    reportId: row.reportId ?? 0,
    header: {
      title: row.reportDisplayName ?? '',
      createdOn: row.createdOn,
      createdBy: row.requestedByUserDTO,
      state: (row.reportState ?? undefined) as ReportState | undefined,
    },
  };
}

export function buildStatementPanelInfo(row: BulkPatientStatementDTO): RunOutputPanelInfo {
  return {
    kind: 'statement',
    bulkPatientStatementId: row.printStatementReportId ?? 0,
    header: {
      title: 'Bulk Patient Statements',
      createdOn: row.createdOn,
      createdBy: row.requestedByUserDTO,
      state: (row.reportState ?? undefined) as ReportState | undefined,
    },
  };
}

function toSummaryTone(tone: string | null | undefined): SummaryTone {
  return tone === 'Warning' || tone === 'Danger' ? tone : 'Default';
}

// Orval marks every field optional/nullable; the UI works with the shape the backend actually sends.
export function normalizeRunOutput(dto: ReportOutputDTO): RunOutput {
  return {
    message: dto.message ?? null,
    summary: (dto.summary ?? []).map((item) => ({
      label: item.label ?? '',
      value: item.value ?? '',
      tone: toSummaryTone(item.tone),
    })),
    settings: dto.settings ?? {},
    files: (dto.files ?? []).map((file) => ({
      reportOutputFileId: file.reportOutputFileId ?? null,
      fileName: file.fileName ?? '',
      contentType: file.contentType ?? '',
      sizeBytes: file.sizeBytes ?? null,
      itemCount: file.itemCount ?? null,
      canPreview: file.canPreview ?? false,
    })),
  };
}
